import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserInformation, setDashboardContext, setEffectivePermissions } from "../../../redux-store/ReduxStore.js";
import { resolveLandingPath } from "../../../utils/rbacUtils.js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope, faClock } from "@fortawesome/free-regular-svg-icons";

import { PrimaryButton, SecondaryButton, LinkButton } from "../../../components/button/Button.jsx";
import { Input, Password } from "../../../components/form/Input.jsx";
import InfoTooltip from "../../../components/tooltip/InfoTooltip.jsx";
import { passwordValidations, isPasswordValid } from "../../../utils/passwordPolicy.js";
import { UserAuth } from "./../../../authentication/AuthContext.jsx";
import { getUser } from "./../../../services/UserProfileService.jsx";
import { getEffectivePermissions } from "../../../services/admin-settings/UserPermissionService.jsx";
import ThirdPartyLogins from "./ThirdPartyLogins.jsx";
import { GeoPlayLogo } from "../../../shell/header/GeoPlayLogo.jsx";
import EmailIcon from "../../../assets/common/email.svg?react";
import CircleCheckIcon from "../../../assets/common/check-circle.svg?react";
import LockIcon from "../../../assets/common/lock.svg?react";
import ShieldCheckIcon from "../../../assets/common/check-shield.svg?react";

import "./SignupLogin.css";
import { SUPER_USER_LANDING } from '../../../config/accessControl.js';

const Signup = ({ onSwitchToLogin, navigatedFrom = 'home' }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [lastUser, setLastUser] = useState(null);

    const { signUp, sendVerificationEmail, logOut } = UserAuth();
    const dispatch = useDispatch();

    useEffect(() => {
        let timerInterval;
        if (verificationSent && resendTimer > 0) {
            timerInterval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timerInterval);
    }, [verificationSent, resendTimer]);

    const handleClose = () => {
        setVerificationSent(false);
    };

    const validateEmail = (email) => {
        const re = /^[a-zA-Z0-9._%+-]+@(liquidnitrogames\.com|liquidnitro\.games)$/;
        return re.test(email);
    };

    const checkUserExists = async (user) => {
        try {
            const obj = {
                userId: user.email,
                accessToken: user.accessToken,
            };

            const userData = await getUser(obj);

            if (navigatedFrom === 'home') {
                if (userData && userData.user) {
                    const orgs = userData.user.orgUserStates
                        ? Object.entries(userData.user.orgUserStates).map(([orgId, orgState]) => ({ orgId, orgState }))
                        : [];
                    const firstOrgId = orgs.length > 0 ? orgs[0].orgId : null;
                    const persistedOrgId = userData.user.activeOrgId || firstOrgId;
                    dispatch(setUserInformation({
                        userId: userData.user.userId || null,
                        userName: userData.user.userName || null,
                        orgs: orgs,
                        roleId: userData.user.roleId || null,
                        superUser: userData.user.superUser || false
                    }));
                    const dashCtx = {
                        selectedOrgId: persistedOrgId,
                        selectedGameId: userData.user.activeGameId || null,
                        selectedGameName: null,
                        selectedGameClientId: userData.user.activeClientId || null,
                        selectedRegionIds: []
                    };
                    dispatch(setDashboardContext(dashCtx));

                    // Platform super-users (control plane only) never go through tenant onboarding —
                    // no org, game, or SDK setup. The backend auto-provisions them from the allowlist
                    // the first time we identify them, so the very first third-party sign-up already
                    // comes back flagged. Without this they'd fall into the no-org branch below and
                    // land on /demo's "No active request" card. Mirrors Login.jsx.
                    if (userData.user.superUser) {
                        navigate(SUPER_USER_LANDING);
                    } else if (persistedOrgId) {
                        let perms = null;
                        try {
                            perms = await getEffectivePermissions({ accessToken: user.accessToken });
                            dispatch(setEffectivePermissions(perms));
                        } catch (e) {
                            console.error("Error fetching permissions on signup redirect:", e);
                        }
                        navigate(resolveLandingPath(dashCtx));
                    } else {
                        navigate('/demo');
                    }
                } else {
                    navigate('/signup');
                }
            }
            return userData;
        } catch (error) {
            // Only a definitive 404 means this account should onboard — a server/network failure must
            // never drop an authenticated user into org creation (that's how duplicate orgs get made).
            if (navigatedFrom === 'home') {
                if (error?.response?.status === 404) {
                    navigate('/signup');
                } else {
                    setError("We couldn't reach the server. Please try again in a moment.");
                    setLoading(false);
                }
            }
            return null;
        }
    };

    const handleThirdPartySignupSuccess = async (user) => {
        setError("");
        setLoading(true);
        try {
            await checkUserExists(user);
            handleClose();
        } catch (error) {
            setLoading(false);
        }
    };

    const handleThirdPartySignupError = (errorMessage) => {
        setError(errorMessage);
        setLoading(false);
    };

    const handleSignup = async (e) => {
        e.preventDefault();

        if(!email || !password || !confirmPassword) {
            setError("All fields are required");
            return;
        }

        if (!validateEmail(email)) {
            setError("Only work email addresses are allowed.");
            return;
        }

        if (!isPasswordValid(password)) {
            setError("Password does not meet requirements.");
            return;
        }

        if(password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const userCredential = await signUp(email, password);
            const user = userCredential.user;
            setLastUser(user);

            if (navigatedFrom === 'home') {
                await handleEmailVerification(user);
            } else {
                setLoading(false);
            }

        } catch (error) {
            let errorMessage = error.code
                ? "Sign up failed. Please try again."
                : "We couldn't reach the server. Please try again in a moment.";

            if (error.code) {
                switch (error.code) {
                    case 'auth/invalid-email':
                        errorMessage = "Enter a valid email address.";
                        break;
                    case 'auth/email-already-in-use':
                        errorMessage = "Email is already registered. Try logging in instead.";
                        break;
                    case 'auth/weak-password':
                        errorMessage = "Password is too weak. Please choose a stronger one.";
                        break;
                    case 'auth/too-many-requests':
                        errorMessage = "Too many attempts. Please try again later.";
                        break;
                    case 'auth/operation-not-allowed':
                        errorMessage = "Email sign-up is currently unavailable. Please use Google or Microsoft.";
                        break;
                    default:
                        break;
                }
            }
            setError(errorMessage);
            setLoading(false);
        }
    };

    const signupComponent = () => {
        return (
            <div className="login-signup-form-container">
                <div className="login-signup-form-super-title-row">
                    <span className="login-signup-brand-mark"><GeoPlayLogo /></span>
                    <h2 className="login-signup-form-super-title">Sign Up</h2>
                </div>
                <ThirdPartyLogins
                    onSuccess={handleThirdPartySignupSuccess}
                    onError={handleThirdPartySignupError}
                    disabled={loading}
                />
                <div className="login-signup-divider">OR</div>
                <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="email"
                    placeholder="Enter Email"
                    label='Email'
                    isRequired
                    icon={<FontAwesomeIcon icon={faEnvelope} />}
                />
                {/* labelEnd keeps the "i" inline so it reserves space instead of overlapping. */}
                <Password
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create Password"
                    label='Password'
                    isRequired
                    icon={<LockIcon />}
                    labelEnd={
                        <InfoTooltip
                            className="signup-password-info"
                            label="Password requirements"
                            text={
                                <div className="login-signup-form-container-section">
                                    {passwordValidations.map((validation, index) => (
                                        <div
                                            key={index}
                                            className={`signup-form-password-validation-item ${validation.check(password) ? 'valid' : 'invalid'}`}
                                        >
                                            <span>{validation.check(password) ? <CircleCheckIcon/> : <FontAwesomeIcon icon={faXmark} />}</span>
                                            {validation.text}
                                        </div>
                                    ))}
                                </div>
                            }
                        />
                    }
                />
                <Password
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter Password"
                    label='Confirm Password'
                    isRequired
                    icon={<ShieldCheckIcon />}
                />
                {error && <div className="error-message">{error}</div>}
                <PrimaryButton onClick={handleSignup} disabled={loading} isEnterTriggered isFullWidth>
                    {loading ? "Signing Up..." : (navigatedFrom === 'home' ? "Send Verification Link" : "Sign Up")}
                </PrimaryButton>
                {/* Mirrors the Login page: full-width primary button, then a
                    row with the helptext (centered) beside the secondary CTA. */}
                {onSwitchToLogin && (
                    <div className="login-signup-form-container-section-row">
                        <p className="login-signup-form-helptext">Already have an account?</p>
                        <SecondaryButton onClick={onSwitchToLogin} isFullWidth>
                            Login
                        </SecondaryButton>
                    </div>
                )}
            </div>
        );
    };

    const handleEmailVerification = async (user) => {
        if(!verificationSent) setVerificationSent(true);
        // Cooldown starts on the tap. Waiting for the send to resolve left the "Resend Link"
        // control on screen and tappable for the whole round-trip; rolled back below on failure.
        setResendTimer(60);
        try {
            await sendVerificationEmail(user);
        } catch (err) {
            setResendTimer(0);
            const errorMessage = err?.code === 'auth/too-many-requests'
                ? 'Too many requests. Please wait a moment before requesting another link.'
                : 'We could not send the verification link. Please try again in a moment.';
            setError(errorMessage);
            setLoading(false);
        }
    }

    const handleEmailVerificationClose = async () => {
        await logOut();
        setLastUser(null);
        handleClose();
        navigate('/');
    }

    const emailVerificationComponent = () => {
        return (
            <div className="login-signup-form-container">
                <div className="login-signup-form-icon-container">
                    <EmailIcon/>
                </div>
                <h2 className="login-signup-form-title">Verify Your Email</h2>
                <p className="login-signup-form-helptext">We've successfully registered your account. A verification link has been dispatched to:</p>
                <div className="login-signup-form-email-badge">{email || ''}</div>
                {error && <div className="error-message">{error}</div>}
                <p className="login-signup-form-helptext">Didn't receive the link?</p>
                {resendTimer ?
                    <div className="login-signup-form-retry">
                        <FontAwesomeIcon icon={faClock} />
                        Try again in <span>{resendTimer}s</span>
                    </div>
                    : <LinkButton onClick={() => handleEmailVerification(lastUser)}>Resend Link</LinkButton>}
                <SecondaryButton onClick={handleEmailVerificationClose} isFullWidth>Back To Home Page</SecondaryButton>
                <p className="login-signup-form-helptext">Please check your inbox (and spam folder). You must verify account to access all platform features.</p>
            </div>
        );
    }

    return (
        <div className="login-signup-page">
            {!verificationSent ? signupComponent() : emailVerificationComponent()}
        </div>
    );
};

export default Signup;